import React, { useEffect, useState } from "react";
import AuthenticationService from "../../../shared/services/authentication";
import { ColSize, Form, InputForm, RowSize } from "../../../shared/components";
import { ActionButton } from "@hdi-seguros/web-components.action-button";
import { LogoColorfull } from "../../../assets";
import { Image, Input } from "antd";
import { ValidateForm } from "../../../shared/utils";
import { Form as FormAnt, message } from "antd";
import "./index.css";
import {
  EyeFilled,
  EyeInvisibleFilled,
  LockOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { translateCognitoError } from "../../../shared/contexts/auth/utils/utils";
import { StepsForgotPassword } from "../../../shared/interfaces/enums/step-forgot-password-enum";

interface ForgotPasswordProps {
  onBackToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordProps> = ({
  onBackToLogin,
}) => {
  const [step, setStep] = useState<StepsForgotPassword>(1);
  const [loading, setLoading] = useState(false);
  const validateForm = new ValidateForm();
  const [form] = FormAnt.useForm();
  const [verificationCode, setVerificationCode] = useState("");
  const [forgotPasswordTitle, setForgotPasswordTitle] = useState("");
  const handleForgotPassword = async () => {
    setLoading(true);

    const username = form.getFieldValue("username") as string;

    if (!username) {
      message.error("Ingrese un nombre de usuario valido");
      setLoading(false);
      return;
    }

    try {
      await AuthenticationService.forgotPassword(username);
      setStep(2);
      message.success(
        "Se envió un código de verificación a tu correo o teléfono registrado."
      );
    } catch (err: any) {
      message.error(
        translateCognitoError(err.code) ||
          "Ocurrió un error al enviar el código."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    switch (step) {
      case StepsForgotPassword.ONE:
        setForgotPasswordTitle('Te enviaremos un código de recuperación a tu casilla')
        break;
      case StepsForgotPassword.TWO:
        setForgotPasswordTitle('Ingresa el código de recuperación que has recibido')
        break;
      default:
        setForgotPasswordTitle('Ya puedes cambiar tu contraseña')
        break;
    }
  },[step])

  const handleConfirmNewPassword = async () => {
    setLoading(true);
    const username = form.getFieldValue("username") as string;
    const newPassword = form.getFieldValue("new-password") as string;
    const confirmNewPassword = form.getFieldValue(
      "new-password-again"
    ) as string;
    if (newPassword !== confirmNewPassword) {
      message.error("Las contraseñas son diferentes");
      setLoading(false);
      return;
    }
    try {
      await AuthenticationService.confirmForgotPassword(
        username,
        verificationCode,
        newPassword
      );
      message.success(
        "Contraseña restablecida con éxito. Ya puedes iniciar sesión."
      );
      setStep(1);
      onBackToLogin();
    } catch (err: any) {
      if (err.code === "CodeMismatchException") {
        setStep(2);
      }
      message.error(
        translateCognitoError(err.code) ||
          "Ocurrió un error al cambiar la contraseña."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      formProps={{
        form,
        autoComplete: "off",
        name: "login",
        onFinish: handleConfirmNewPassword,
      }}
    >
      <RowSize className="login_form-container">
        <div>
          <ColSize size={100}>
            <div className="login_form_logo-container">
              <Image
                src={LogoColorfull}
                width={200}
                height={"auto"}
                preview={false}
              />
            </div>
            <div className="login_form-title-container">
              <h2>
                {forgotPasswordTitle}
              </h2>
            </div>
            {step === StepsForgotPassword.ONE && (
              <ColSize size={100}>
                <InputForm
                  formProps={{
                    name: "username",
                    rules: validateForm.generateRules({
                      label: "nombre de usuario",
                      rules: ["required", "notContainSpaces"],
                    }),
                  }}
                  inputProps={{
                    autoComplete: "username",
                    placeholder: "Ingrese su nombre de usuario",
                    prefix: <MailOutlined />,
                    style: { height: 45, width: 300 },
                  }}
                />
                <div className="forgot-password_step1-button">
                  <ActionButton
                    label="Enviar código"
                    onClick={handleForgotPassword}
                    loading={loading}
                    width={300}
                    height={40}
                    className="login-form_button"
                  />
                </div>
              </ColSize>
            )}
            {step === StepsForgotPassword.TWO && (
              <div>
                <ColSize size={100}>
                  <p>Código de Verificación</p>
                  <Input.OTP
                    formatter={(str: string) => str.toUpperCase()}
                    value={verificationCode}
                    onChange={(value) => setVerificationCode(value)}
                  />
                </ColSize>
                <div className="forgot-password_step1-button">
                  <ActionButton
                    disabled={verificationCode.length !== 6}
                    className="forgot-password_step2-button"
                    label="Siguiente"
                    onClick={() => setStep(3)}
                    width={300}
                    height={40}
                  />
                </div>
              </div>
            )}
            {step === StepsForgotPassword.THREE && (
              <div>
                <ColSize size={100}>
                  <InputForm
                    formProps={{
                      name: "new-password",
                      rules: validateForm.generateRules({
                        label: "contraseña",
                        rules: ["required", "notContainSpaces"],
                      }),
                    }}
                    isPassword
                    inputPasswordProps={{
                      autoComplete: "new-password",
                      placeholder: "Nueva Contraseña",
                      prefix: <LockOutlined />,
                      style: { height: 45, width: 300 },
                      iconRender: (visible) =>
                        visible ? <EyeFilled /> : <EyeInvisibleFilled />,
                    }}
                  />

                  <InputForm
                    formProps={{
                      name: "new-password-again",
                      rules: validateForm.generateRules({
                        label: "contraseña",
                        rules: ["required", "notContainSpaces"],
                      }),
                    }}
                    isPassword
                    inputPasswordProps={{
                      autoComplete: "new-password-again",
                      placeholder: "Nueva contraseña",
                      prefix: <LockOutlined />,
                      style: { height: 45, width: 300 },
                      iconRender: (visible) =>
                        visible ? <EyeFilled /> : <EyeInvisibleFilled />,
                    }}
                  />
                </ColSize>
                <div className="forgot-password_step3-buttons">
                  <ActionButton
                    label="Volver atras"
                    type="secondary"
                    onClick={() => setStep(2)}
                    height={40}
                  />
                  <ActionButton
                    label="Cambiar contraseña"
                    type="primary"
                    htmlType="submit"
                    height={40}
                  />
                </div>
              </div>
            )}
            <ActionButton
              type="text"
              label="¿Quieres iniciar sesión?"
              onClick={onBackToLogin}
              className="forgot-password_text-button"
            />
          </ColSize>
        </div>
      </RowSize>
    </Form>
  );
};

export default ForgotPasswordForm;
